import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { artifactClient } from '../lib/connect/clients';
import { sameOriginStorageUrl } from '../lib/storageUrl';
/**
 * Resolve an artifact to a browser-usable preview URL.
 *
 * Calls tesoro's `GenerateDownloadUrl` (via sabaody) to get a short-lived
 * MinIO presigned GET URL, then fetches the bytes and wraps them in a blob
 * URL so existing `<img src>` / `<iframe src>` consumers don't have to
 * change.
 *
 * The presigned URL itself is enough to render in an `<img>` tag, but
 * piping through a blob URL keeps caching, lifecycle, and CORS edge cases
 * symmetric with the previous /api/attachment/:id/stream path.
 */ async function fetchAttachment(attachmentId) {
    const dl = await artifactClient.generateDownloadUrl({
        artifactId: attachmentId
    });
    if (!dl.url) {
        throw new Error('No download URL returned for artifact');
    }
    const response = await fetch(sameOriginStorageUrl(dl.url));
    if (!response.ok) {
        throw new Error(`Failed to fetch attachment: ${response.statusText}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
}
/**
 * Hook to fetch and preview attachments with React Query
 * Automatically manages blob URL lifecycle (creation and cleanup)
 *
 * @param options - Configuration options
 * @returns React Query result with blob URL
 */ export function useAttachmentPreview({ attachmentId, enabled = true, cacheScope }) {
    const query = useQuery({
        queryKey: cacheScope ? [
            'attachment-preview',
            attachmentId || 'none',
            cacheScope
        ] : [
            'attachment-preview',
            attachmentId || 'none'
        ],
        queryFn: ()=>{
            if (!attachmentId) {
                throw new Error('Attachment ID is required');
            }
            return fetchAttachment(attachmentId);
        },
        enabled: enabled && !!attachmentId,
        ...cacheScope ? {
            retry: false
        } : {},
        staleTime: cacheScope ? 0 : 5 * 60 * 1000,
        gcTime: cacheScope ? 0 : 10 * 60 * 1000
    });
    // Cleanup blob URL when component unmounts, data changes, or when disabled
    useEffect(()=>{
        // If we have a blob URL but the query is now disabled, revoke it
        if (!enabled && query.data) {
            URL.revokeObjectURL(query.data);
        }
        return ()=>{
            if (query.data) {
                URL.revokeObjectURL(query.data);
            }
        };
    }, [
        query.data,
        enabled
    ]);
    return query;
}
